/**
 * Phone numbers are stored twice: as typed/displayed and normalised to E.164
 * (+48XXXXXXXXX) so that search ignores spaces, dashes and the country prefix.
 */

/** Polish mobile prefixes (first two digits of the national number), formatted 3-3-3. */
const PL_MOBILE_PREFIXES = new Set([
  '45', '50', '51', '53', '57', '60', '66', '69', '72', '73', '78', '79', '88',
]);

/** Returns E.164 (`+48600123456`) or `null` when the input cannot be a phone number. */
export function normalizePhone(input: string): string | null {
  const trimmed = input.trim();
  if (trimmed === '') return null;
  // Allow digits, spaces, dashes, dots, slashes, parentheses and one leading plus.
  if (!/^\+?[\d\s\-./()]+$/.test(trimmed)) return null;

  let digits = trimmed.replace(/\D/g, '');
  const hasPlus = trimmed.startsWith('+');

  if (!hasPlus && digits.startsWith('00')) {
    digits = digits.slice(2);
    return isPlausibleInternational(digits) ? `+${digits}` : null;
  }
  if (hasPlus) {
    return isPlausibleInternational(digits) ? `+${digits}` : null;
  }
  // Polish national number, optionally with a leading trunk zero ("0 600 ...").
  if (digits.length === 10 && digits.startsWith('0')) digits = digits.slice(1);
  if (digits.length === 9) return `+48${digits}`;
  if (digits.length === 11 && digits.startsWith('48')) return `+${digits}`;
  return null;
}

function isPlausibleInternational(digits: string): boolean {
  if (digits.startsWith('48')) return digits.length === 11;
  return digits.length >= 8 && digits.length <= 15;
}

/** `+48600123456` → `+48 600 123 456`; `+48123456789` → `+48 12 345 67 89`. */
export function formatPhone(e164: string): string {
  if (!e164.startsWith('+48') || e164.length !== 12) return e164;
  const n = e164.slice(3);
  if (PL_MOBILE_PREFIXES.has(n.slice(0, 2))) {
    return `+48 ${n.slice(0, 3)} ${n.slice(3, 6)} ${n.slice(6)}`;
  }
  return `+48 ${n.slice(0, 2)} ${n.slice(2, 5)} ${n.slice(5, 7)} ${n.slice(7)}`;
}

/** Digits to match against normalised numbers in search ("600-12" → "60012"). */
export function phoneSearchDigits(input: string): string {
  return input.replace(/\D/g, '');
}

/** `tel:` URI for a stored (normalised or display) number. */
export function telHref(phone: string): string {
  const normalized = normalizePhone(phone);
  return `tel:${normalized ?? phone.replace(/[^\d+]/g, '')}`;
}
